/**
 * Deploy-skew recovery.
 *
 * A window left open across a deploy is still pinned to the build it loaded
 * with. Next.js code-splits every route, so the first lazy `import()` after
 * the old deployment's assets expire 404s, the resulting ChunkLoadError
 * bubbles past every boundary, and the whole UI is replaced by "Application
 * error: a client-side exception has occurred". The only escape is a manual
 * Cmd+R — which desktop users, whose window is never closed (closing only
 * hides it, so the renderer lives on for realtime), have no reason to try.
 *
 * A reload fetches the current document and with it the current chunk names,
 * so a chunk failure is recoverable, not fatal. The sessionStorage stamp caps
 * that at one recovery reload per cooldown, so a build that is genuinely
 * broken (chunk 404s on every load) can't spin the window in a reload loop —
 * it degrades to the error screen we already had.
 */

export const CHUNK_RELOAD_KEY = "spoke.chunkReloadAt";
export const RELOAD_COOLDOWN_MS = 10 * 60 * 1000;

// Bundler-agnostic on purpose: webpack throws a named ChunkLoadError,
// Turbopack throws "Failed to load chunk … from module …", and a native
// dynamic import that 404s throws the browser's own wording. window.onerror
// hands us only the message string, so plain strings count too.
const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk \S+ failed/i,
  /Failed to load chunk/i,
  /Loading CSS chunk/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

function describe(err: unknown): string {
  if (typeof err === "string") return err;
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (err && typeof err === "object") {
    const { name, message } = err as { name?: unknown; message?: unknown };
    if (typeof message === "string") return `${typeof name === "string" ? name : ""}: ${message}`;
  }
  return "";
}

/** Is this the failure of a code-split chunk rather than an app-level error? */
export function isChunkLoadError(err: unknown): boolean {
  const text = describe(err);
  if (!text) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

function lastAttemptAt(): number | null {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Reload to pick up the current deployment if `err` is a chunk failure and we
 * haven't already tried within the cooldown. Returns whether a reload was
 * fired, so callers can keep rendering their error UI when it wasn't.
 */
export function recoverFromChunkError(err: unknown): boolean {
  if (typeof window === "undefined") return false;
  if (!isChunkLoadError(err)) return false;

  const last = lastAttemptAt();
  if (last !== null && Date.now() - last < RELOAD_COOLDOWN_MS) {
    console.warn("[chunk-reload] chunk failed again inside the cooldown — not reloading");
    return false;
  }

  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // Private mode / storage full: the cooldown is best-effort, but a reload
    // without it could loop, so don't fire one we can't record.
    return false;
  }

  console.warn("[chunk-reload] stale deployment — reloading to pick up the current build");
  window.location.reload();
  return true;
}
